'use client';

import { useAppPersistenceStore } from '@ninjacart/nc-nk-store';
import {
  HomeFilled,
  HomeUnFilled,
  ProductFilledIcon,
  ProductNonFilledIcon,
  ProfileFilled,
  ProfileUnFilled,
  QnAFilled,
  QnAUnFilled,
  Text,
  useTheme,
  VideoFilled,
  VideoUnFilled,
  XStack,
  YStack
} from '@ninjacart/nc-nk-ui';
import { useThemeSetting } from '@tamagui/next-theme';
import Link from 'next/link';
import { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePathname } from 'solito/navigation';

const LazyBannerAds = lazy(() =>
  import('@ninjacart/nc-nk-web-deps').then((module) => ({ default: module.BannerAds }))
);
interface NavItem {
  label: string;
  path: string;
  tkey: string;
  filledIcon: React.ComponentType<any>;
  unfilledIcon: React.ComponentType<any>;
}

const navItems: NavItem[] = [
  {
    label: 'Home',
    tkey: 'page.home',
    path: '/',
    filledIcon: HomeFilled,
    unfilledIcon: HomeUnFilled,
  },
  {
    label: 'Q&A',
    path: '/qna',
    tkey: 'page.questions',
    filledIcon: QnAFilled,
    unfilledIcon: QnAUnFilled,
  },
  {
    label: 'Products',
    path: '/products',
    tkey: 'page.product',
    filledIcon: ProductFilledIcon,
    unfilledIcon: ProductNonFilledIcon,
  },
  {
    label: 'Discover',
    path: '/discover',
    tkey: 'page.videos',
    filledIcon: VideoFilled,
    unfilledIcon: VideoUnFilled,
  },
  {
    label: 'Profile',
    path: '/profile',
    tkey: 'page.profile',
    filledIcon: ProfileFilled,
    unfilledIcon: ProfileUnFilled,
  },
];

export default function WebTabsLayout({
  children,
  modal,
}: {
  children: React.ReactNode;
  modal: React.ReactNode;
}) {
  // console.log('Rendering tabs layout!!!');
  const currentPath = usePathname();
  const { t } = useTranslation();
  const appLanguage = useAppPersistenceStore((state) => state.appLanguage);
  const showBottomBar = navItems.some((item) => item.path === currentPath);
  const [isClient, setIsClient] = useState(false);
  const [iconColor, setIconColor] = useState('#1D130A'); // Default light theme color
  const theme = useTheme();
   const themeSetting = useThemeSetting();

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Update icon color when theme changes
  useEffect(() => {
    setIconColor(theme['color1']?.val || '#1D130A');
  }, [themeSetting]);

  return (
    <YStack fullscreen>
      <YStack 
        $gtSm={{ paddingHorizontal: 80 }} 
        flex={1}
      >
        <XStack flex={1}>
          {/* Sidebar - Left */}
          <YStack
            width={0}
            display="none"
            $gtSm={{
              display: 'flex',
              width: 200,
              backgroundColor: '$background',
              paddingVertical: '$4',
            }}
            borderRightWidth={1}
            borderColor="$layoutBorderColor"
          >
            {navItems.map((item) => {
              const Icon = currentPath === item.path ? item.filledIcon : item.unfilledIcon;
              const currentIconColor = currentPath === item.path ? '#368665' : iconColor;
              return (
                <Link
                  key={`sidebar-${item.path}`}
                  href={item.path}
                  style={{ textDecoration: 'none' }}
                >
                  <XStack
                    alignItems="center"
                    justifyContent="flex-start"
                    width="85%"
                    borderRadius={9999}
                    paddingVertical={12}
                    paddingHorizontal={16}
                    marginVertical={4}
                    cursor="pointer"
                    hoverStyle={{ backgroundColor: '$sideBarHoverColor' }}
                  >
                    <XStack alignItems="center" gap={12}>
                      <Icon width={28} height={28} color={currentIconColor} />
                      <Text
                        fontSize={18}
                        color={currentPath === item.path ? '$primary' : '$usertextColor'}
                        fontWeight={currentPath === item.path ? '700' : '400'}
                      >
                        {t(item.tkey)}
                      </Text>
                    </XStack>
                  </XStack>
                </Link>
              );
            })}
          </YStack>

          {/* Main Content - Middle */}
          <YStack
            flex={1}
            alignItems="center"
            height="100%"
            borderRightWidth={1}
            borderColor="$layoutBorderColor"
          >
            <YStack width="100%" maxWidth={600} padding="$4" flex={1}>
              {children}
              {modal}
            </YStack>
          </YStack>

          {/* Right Column - Ads / Suggestions */}
          <YStack
            display="none"
            $gtMd={{
              display: 'flex',
              width: 320, // enough for 300px ads
              padding: '$4',
              backgroundColor: '$background',
            }}
          >
            <LazyBannerAds
              deviceWidth={320}
              itemKey={`skyscraper-ad`}
              unitId={'/23198695442/kisan_msite/main_layout_skyscraper'}
              screenName={'sidebar-skyscraper'}
              webAdSizeDefs={[
                [300, 600],
                [160, 600],
                [120, 600],
              ]}
            />
          </YStack>
        </XStack>
      </YStack>

      {/* Bottom Bar */}
      {showBottomBar && (
        <YStack
          // position='absolute'
          bottom={0}
          left={0}
          right={0}
          height={60}
          zIndex={10}
          $gtSm={{ display: 'none' }}
          bg="$background"
          borderTopWidth={1}
          borderColor="$gray"
          style={{ position: 'fixed' }}
        >
          <XStack flex={1} height="100%">
            {navItems.map((item) => {
              const Icon = currentPath === item.path ? item.filledIcon : item.unfilledIcon;
              const currentIconColor = currentPath === item.path ? '#368665' : iconColor;
              return (
                <YStack
                  flex={1}
                  alignItems="center"
                  key={`bottombar-${item.path}`}
                  justifyContent="center"
                >
                  <Link
                    href={item.path}
                    style={{
                      display: 'flex',
                      textDecoration: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Icon width={28} height={28} color={currentIconColor} />
                    <Text
                      fontSize={appLanguage == 'ta' ? 10 : 16}
                      paddingTop={item.path == '/mic' ? 30 : 0}
                      color={currentPath === item.path ? '$primary' : '$profileNavTabColor'}
                    >
                      {t(item.tkey)}
                    </Text>
                  </Link>
                </YStack>
              );
            })}
          </XStack>
        </YStack>
      )}
    </YStack>
  );
}
